import React, { MouseEventHandler, useState } from "react";
import Country from "../../model/Country";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import { useApiClient } from "../../contexts/ApiClientContext";
import SelectWithSearch from "../common/select_search/SelectSearchSide";
import SelectSearchOption from "../common/select_search/SelectSearchOption";
import Province from "../../model/Province";
import Municipality from "../../model/Municipality";
import AdministrativeStructure from "../../model/AdministrativeStructure";

interface ISearchFilterAdministrativeProps {
  onChange: (
    inputID: string,
    newValue: number | string | AdministrativeStructure | undefined
  ) => void;
  selectedCountry: Country | undefined;
  selectedProvince: Province | undefined;
  selectedMunicipality: Municipality | undefined;
  selectTopCSS?: string | undefined;
}

/* Process filters based on string values */
function SearchFilterAdministrative({
  onChange,
  selectedCountry,
  selectedProvince,
  selectedMunicipality,
  selectTopCSS = undefined,
}: ISearchFilterAdministrativeProps) {
  const [keywordCountry, setKeywordCountry] = useState("");
  const [keywordProvince, setKeywordProvince] = useState("");
  const [keywordMunicipality, setKeywordMunicipality] = useState("");

  const [countries, setCountries] = useState<Country[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);

  // reference to the ApiClient instance
  const apiClient = useApiClient();

  const search = async (fieldId: string, keyword: string) => {
    if (fieldId === "country") {
      const countriesInfo = await apiClient?.get<PagedData<Country[]>>({
        url: APIEndPoints.COUNTRIES.SEARCH(keyword),
      });
      setKeywordCountry(keyword);
      setCountries(countriesInfo.items);
    } else if (fieldId === "province") {
      const provincesInfo = await apiClient?.get<PagedData<Province[]>>({
        url: APIEndPoints.PROVINCES.SEARCH(keyword, selectedCountry?.country!),
      });
      setKeywordProvince(keyword);
      setProvinces(provincesInfo.items);
    } else if (fieldId === "municipality") {
      const municipalitiesInfo = await apiClient?.get<
        PagedData<Municipality[]>
      >({
        url: APIEndPoints.MUNICIPALITIES.SEARCH(
          keyword,
          selectedCountry?.country!,
          selectedProvince?.admin1!
        ),
      });
      setKeywordMunicipality(keyword);
      setMunicipalities(municipalitiesInfo.items);
    }
  };

  const filteredOnChange = (
    inputID: string,
    newValue: number | string | undefined
  ) => {
    // in case the option "clear filter" has been selected we have "undefined" as value
    if (!newValue) {
      // cascade the updates
      resetAllFilters(inputID, newValue);
      onChange(inputID, newValue);
      return;
    }
    // identify the object by newValue which is the ID
    var lookInto: AdministrativeStructure[] | undefined = undefined;
    if (inputID === "country") {
      lookInto = countries;
    } else if (inputID === "province") {
      lookInto = provinces;
    } else if (inputID === "municipality") {
      lookInto = municipalities;
    }

    if (Array.isArray(lookInto) && lookInto.length > 0) {
      const record = lookInto?.filter((r) => r.geonameid === newValue);
      if (Array.isArray(record) && record.length > 0) {
        resetAllFilters(inputID, record[0]);
        onChange(inputID, record[0]);
      }
    }
  };

  const resetAllFilters = (inputID: string, newValue: any) => {
    // we are not reseting current level if the newValue is not undefined
    if (newValue) {
      if (inputID === "country") {
        inputID = "province";
      } else if (inputID === "province") {
        inputID = "municipality";
      } else if (inputID === "municipality") {
        inputID = "";
      }
    }

    // cascade the updates
    switch (inputID) {
      // @ts-ignore
      case "country":
        setKeywordCountry("");
        setCountries([]);
      // FALLS THROUGH to "province"
      // @ts-ignore
      case "province":
        setKeywordProvince("");
        setProvinces([]);
      // FALLS THROUGH to "municipality"
      case "municipality":
        setKeywordMunicipality("");
        setMunicipalities([]);
    }
  };

  const countriesOptions = countries.map((country) => {
    return new SelectSearchOption(country.name, country.geonameid);
  });

  const provincesOptions = provinces.map((province) => {
    return new SelectSearchOption(province.name, province.geonameid);
  });

  const municipalitiesOptions = municipalities.map((municipality) => {
    return new SelectSearchOption(municipality.name, municipality.geonameid);
  });

  return (
    <div className="checkbox-filter">
      <SelectWithSearch
        id="country"
        label="Country"
        value={selectedCountry?.geonameid}
        noValueMessage={selectedCountry?.name ?? "All countries"}
        options={countriesOptions}
        onChange={filteredOnChange}
        onSearch={search}
        topCSS={selectTopCSS}
      />

      {selectedCountry?.geonameid && (
        <SelectWithSearch
          id="province"
          label="Province"
          value={selectedProvince?.geonameid}
          noValueMessage={selectedProvince?.name ?? "All provinces"}
          options={provincesOptions}
          onChange={filteredOnChange}
          onSearch={search}
          topCSS={selectTopCSS}
        />
      )}

      {selectedProvince?.geonameid && (
        <SelectWithSearch
          id="municipality"
          label="Municipality"
          value={selectedMunicipality?.geonameid}
          noValueMessage={selectedMunicipality?.name ?? "All municipalities"}
          options={municipalitiesOptions}
          onChange={filteredOnChange}
          onSearch={search}
          topCSS={selectTopCSS}
        />
      )}
    </div>
  );
}

export default SearchFilterAdministrative;
